import CircularProgress from "@mui/material/CircularProgress";
import {
  useGetHomesQuery,
  useLikeHomeMutation,
} from "src/redux/service/home.service";
import Grid from "@mui/material/Grid";
import Header from "src/components/header/header";
import Footer from "src/components/footer/footer";
import { CarosouelImage } from "./MainPage/Components/Carousel";
import { useLocation } from "react-router-dom";
import NotFoundPage from "./MainPage/Components/NotFound";
import Typography from "@mui/material/Typography";
import HomesMap from "src/components/map/HomesMap";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useState } from "react";

export default function Homes() {
  const [highlightedHomeId, setHighlightedHomeId] = useState<string | null>(null);

  // Get params and query from URL using react magic
  const query = useLocation().search;
  const {
    data: homes,
    isLoading: isHomesLoading,
    isError,
  } = useGetHomesQuery(query);

  const [likeHome, { isLoading: isLikingUpdating, isError: likeError }] =
    useLikeHomeMutation();
  if (isHomesLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }
  if (isError) {
    return <NotFoundPage redirectPath="/homes" />;
  }
  if (!homes || homes.length === 0) {
    return (
      <>
        <Header />
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{
            minHeight: "30vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            // height: "100vh",
            textAlign: "center",
          }}
        >
          <Grid item xs={12}>
            <Typography
              variant="h1"
              component="h2"
              sx={{
                fontSize: "5rem",
                fontWeight: "bold",
                color: "#333",
                pt: "10vh",
              }}
            >
              No homes match your search.
            </Typography>
            <Typography sx={{ fontSize: "1.5rem" }}>
              Please try a different search or adjust your filters.
            </Typography>
          </Grid>
        </Grid>
        <Footer />
      </>
    );
  }
  return (
    <>
      <Header />
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          pt: { xs: 12, md: 14 },
          pb: { xs: 5, md: 8 },
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 0.5 }}
        >
          {homes.length} home{homes.length > 1 ? "s" : ""} available
        </Typography>
        <Typography sx={{ color: "#6b7280", mb: 3 }}>
          Explore by neighborhood and compare options at a glance.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.1fr 1fr" },
            gap: { xs: 3, lg: 4 },
            alignItems: "start",
          }}
        >
          <Box sx={{ order: { xs: 2, lg: 1 } }}>
            <Grid container spacing={3}>
              {homes.map((home) => (
                <Grid item xs={12} sm={6} key={String(home._id)}>
                  <Box
                    onMouseEnter={() => setHighlightedHomeId(String(home._id))}
                    onMouseLeave={() => setHighlightedHomeId(null)}
                    sx={{
                      borderRadius: 3,
                      border:
                        highlightedHomeId === String(home._id)
                          ? "1px solid #3980D1"
                          : "1px solid transparent",
                      boxShadow:
                        highlightedHomeId === String(home._id)
                          ? "0 16px 36px rgba(17, 24, 39, 0.14)"
                          : "0 6px 18px rgba(17, 24, 39, 0.08)",
                      transform:
                        highlightedHomeId === String(home._id)
                          ? "translateY(-3px)"
                          : "translateY(0)",
                      transition: "all 220ms ease",
                      backgroundColor: "#fff",
                      p: 0.75,
                    }}
                  >
                    <CarosouelImage
                      home={home}
                      caption={home?.descriptions?.title || ""}
                      likeFunction={() => {
                        return likeHome({
                          homeId: String(home._id),
                          like: !home?.likedByUser,
                        }).unwrap();
                      }}
                      analyticsSource="homes_results_grid"
                      location={
                        home?.city && home?.country
                          ? `${home.city}, ${home.country}`
                          : undefined
                      }
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Paper
            elevation={0}
            sx={{
              order: { xs: 1, lg: 2 },
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              p: 1,
              position: { lg: "sticky" },
              top: { lg: 100 },
              backgroundColor: "#fff",
            }}
          >
            <HomesMap
              homes={homes}
              height="70vh"
              highlightedHomeId={highlightedHomeId}
              onHomeHoverChange={setHighlightedHomeId}
            />
          </Paper>
        </Box>
      </Box>
      <Footer />
    </>
  );
}
